/**
 * /src/charts/game-console-chart/initializer.js
 * 新仕様 (Role属性ベース) 対応版
 */
import { GameConsoleChart } from './GameConsoleChart.ts';

document.addEventListener('DOMContentLoaded', () => {
	// --- 1. ターゲット要素の取得 (新仕様: Role属性ベース) ---
	// data-chart-role="container" かつ data-chart-type="game-console-chart" の要素を検索
	const chartWrappers = document.querySelectorAll('[data-chart-role="container"][data-chart-type="game-console-chart"]');

	chartWrappers.forEach(wrapper => {
		// このイニシャライザはUI生成ロジックが複雑なため、async/awaitで記述
		const initializeChart = async () => {
			// --- 2. 内部要素の取得 (新仕様: Role属性ベース) ---
			const chartArea = wrapper.querySelector('[data-chart-role="chart-area"]');
			const legendControlsContainer = wrapper.querySelector('[data-chart-role="legend"]');
			const footerControlsContainer = wrapper.querySelector('[data-chart-role="controls"]');

			if (!chartArea || !legendControlsContainer || !footerControlsContainer) {
				console.error('[game-console-chart] Error: Required DOM elements (role=chart-area/legend/controls) not found.');
				return;
			}
			
			// 二重初期化防止
			if (chartArea._chartInstance) {
				return;
			}

			// --- 3. グラフインスタンスの生成とUI用データロード ---
			const chart = new GameConsoleChart(chartArea, {});
			chartArea._chartInstance = chart;
			chartArea.dataset.chartInstance = 'true';

			// UI（凡例など）を生成するために、先にデータを読み込む
			const uiData = await chart.loadDataForUI();
			if (!uiData) {
				console.error('[game-console-chart] Error: Failed to load data for UI creation.');
				return;
			}

			// --- 4. UIセットアップ ---

			// 4a. UIの状態管理とハンドラ定義
			let visibleKeys = new Set(chart.config.defaultVisibleKeys);
			let userHasInteracted = false;
			const handleUserInteraction = () => {
				if (!userHasInteracted) {
					userHasInteracted = true;
					chart.config.autoplay = false;
				}
			};

			// 4b. UI要素（凡例チェックボックス）の動的生成
			const createLegend = () => {
				legendControlsContainer.innerHTML = uiData.seriesOrder.map(key => {
					// 正式名称ではなく、キー（略称）をそのままラベルとして使用する
					const consoleLabel = key;

					const isChecked = visibleKeys.has(key);
					// IDは一意にする必要がある
					const uniqueId = chartArea.id || Math.random().toString(36).substr(2, 9);
					const checkboxId = `legend-checkbox-${uniqueId}-${key}`;

					return `
						<div class="legend-item">
							<input type="checkbox" id="${checkboxId}" value="${key}" ${isChecked ? 'checked' : ''}>
							<label for="${checkboxId}">${consoleLabel}</label>
						</div>
					`;
				}).join('');
			};

			// 4c. UI要素（フッターコントロール）の動的生成
			const createFooterControls = () => {
				footerControlsContainer.innerHTML = `
					<div class="playback-controls">
						<button class="play-pause-button" aria-label="再生/一時停止"></button>
						<input type="range" class="time-slider" min="0" max="0" value="0" step="1">
					</div>
					<div class="bulk-controls">
						<button class="bulk-control-button select-all">すべて選択</button>
						<button class="bulk-control-button deselect-all">すべて解除</button>
					</div>
				`;
			};

			// 4d. UI状態を更新するためのヘルパー関数
			const updateBulkButtonsState = () => {
				const selectAllBtn = footerControlsContainer.querySelector('.select-all');
				const deselectAllBtn = footerControlsContainer.querySelector('.deselect-all');
				if (!selectAllBtn || !deselectAllBtn) return;
				selectAllBtn.disabled = visibleKeys.size === uiData.seriesOrder.length;
				deselectAllBtn.disabled = visibleKeys.size === 0;
			};

			const updateLegendCheckboxes = () => {
				legendControlsContainer.querySelectorAll('input[type="checkbox"]').forEach(cb => {
					cb.checked = visibleKeys.has(cb.value);
				});
			};

			// 4e. UIの初期描画とイベントリスナー設定
			createLegend();
			createFooterControls();
			updateBulkButtonsState();

			legendControlsContainer.addEventListener('change', event => {
				if (event.target.type === 'checkbox') {
					handleUserInteraction();
					const key = event.target.value;
					if (event.target.checked) visibleKeys.add(key);
					else visibleKeys.delete(key);

					chart.setVisibleSeries(visibleKeys);
					updateBulkButtonsState();
				}
			});

			footerControlsContainer.querySelector('.play-pause-button')?.addEventListener('click', () => {
				handleUserInteraction();
				chart.togglePlayPause();
			});

			footerControlsContainer.querySelector('.time-slider')?.addEventListener('input', event => {
				handleUserInteraction();
				chart.seek(parseInt(event.target.value, 10));
			});

			footerControlsContainer.querySelector('.select-all')?.addEventListener('click', () => {
				handleUserInteraction();
				visibleKeys = new Set(uiData.seriesOrder);
				chart.setVisibleSeries(visibleKeys);
				updateLegendCheckboxes();
				updateBulkButtonsState();
			});

			footerControlsContainer.querySelector('.deselect-all')?.addEventListener('click', () => {
				handleUserInteraction();
				visibleKeys.clear();
				chart.setVisibleSeries(visibleKeys);
				updateLegendCheckboxes();
				updateBulkButtonsState();
			});

			// 4f. 描画クラスからの状態変更をUIに反映
			chart.onUpdateUI(state => {
				const slider = footerControlsContainer.querySelector('.time-slider');
				if (!slider) return;

				// 状態クラスはラッパーに付与（CSS制御用）
				wrapper.classList.toggle('is-playing', state.isPlaying);

				const totalFrames = state.totalFrames > 0 ? state.totalFrames - 1 : 0;
				if (slider.max != totalFrames) {
					slider.max = totalFrames;
				}
				slider.value = state.currentFrame;
			});

			// --- 5. グラフの初期描画 ---
			chart.init();
		};

		// --- 定義した非同期関数を実行し、エラーハンドリング ---
		initializeChart().catch(error => {
			console.error('[game-console-chart] Initialization failed:', error);
		});
	});
});